import { Injectable } from "@angular/core";
import {
  I18nTarget,
  IExposedInstanceLanguage,
  II18nTranslation,
  ILanguageCreationDetails,
  ILanguageModificationDetails,
} from "@shared/interfaces/language.interface";
import { catchError, map, Observable, of } from "rxjs";
import { ApiService } from "./api.service";
@Injectable({
  providedIn: "root",
})
export class LanguageService {
  constructor(private api: ApiService) {}

  /**
   * Retrieves all available instance languages
   * @return list of exposed languages
   */
  public getLanguages(): Observable<IExposedInstanceLanguage[]> {
    return this.api.get("instance/languages", undefined, false) as Observable<IExposedInstanceLanguage[]>;
  }

  /**
   * Creates a new language in the instance
   * @param lang Language creation details
   * @return information about the created language
   */
  public createLanguage(lang: ILanguageCreationDetails): Observable<IExposedInstanceLanguage> {
    return this.api.post("instance/languages", lang) as Observable<IExposedInstanceLanguage>;
  }

  /**
   * Updates an existing language
   * @param langId Language identifier
   * @param lang Language modification payload
   * @returns Success or error
   */
  public updateLanguage(langId: number, lang: ILanguageModificationDetails): Observable<unknown> {
    return this.api.patch(`instance/languages/${langId}`, lang) as Observable<unknown>;
  }

  /**
   * Disables a language
   * @param langId Language identifier
   * @returns Success or error
   */
  public deleteLanguage(langId: number): Observable<unknown> {
    return this.api.patch(`instance/languages/${langId}/disable`, undefined) as Observable<unknown>;
  }

  /**
   * Retrieves i18n translations for given languages
   * @param languages Comma-separated language codes
   * @return Observable containing translation map or empty object
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getTranslation(languages: string): Observable<any> {
    return this.api
      .get<II18nTranslation[]>("instance/i18nTranslation", {
        target: I18nTarget.GLORIA_PATIENT_DASHBOARD,
        languages,
      })
      .pipe(
        catchError((error) => {
          console.error("LanguageService", "Server error while downloading translations", error);
          return of(null);
        }),
        map((serverTranslations) => {
          if (!serverTranslations || serverTranslations.length < 1) {
            console.error("LanguageService", "Server sent back empty translations");
            return {};
          }
          return serverTranslations[0].i18n;
        })
      );
  }
}
